import React, { useState, useEffect } from "react";
import {
  Button,
  ButtonSkeleton,
  ToastNotification,
  SkeletonPlaceholder,
  Tile,
  Toggle,
} from "@carbon/react";
import { ArrowLeft } from "@carbon/icons-react";
import { ReactComponent as FuelIcon } from "../../../../assets/icons/fuel.svg";
import CoworkersDataTable from "../../../../components/coworkersDataTable/CoworkersDataTable";
import ChatManagerDataTable from "../../../../components/chatManagerDataTable/ChatManagerDataTable";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { useGetChatClientInfo } from "../../../../hooks/useGetChatClientInfo";
import { useGetChatList } from "../../../../hooks/useGetChatList";
import { useEnableChat } from "../../../../hooks/useEnableChat";
import { queryClient } from "../../../..";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import ClientBindingModal from "../../../../components/clientBindingModal/ClientBindingModal";
import { createCurrencyFormatter } from "../../../../utils/currencyFormatter";
import TopUpFuelModal from "../../../../components/topUpFuelModal/TopUpFuelModal";
import { Slide, ToastContainer, toast } from "react-toastify";
import { saveRoute, clearRoute } from "../../../../store/routeStorage";
import ChatBalanceHistoryDataTable from "../../../../components/chatBalanceHistoryDataTable/ChatBalanceHistoryDataTable";
import DeductFuelModal from "../../../../components/deductFuelModal/DeductFuelModal";
import { normalizeNumber } from "../../../../utils/normalizeNumber";
import { useAddClientSettings } from "../../../../hooks/useAddClientSettings";

export default function ChatInfo() {
  const [clientInfo, setClientInfo] = useState([]);
  const [chatList, setChatList] = useState([]);
  const [isChatActivated, setIsChatActivated] = useState();
  const [toastType, setToastType] = useState(null);
  const [openClientBindingModal, setOpenClientBindingModal] = useState(true);
  const [openTopUpFuelModal, setOpenTopUpFuelModal] = useState(false);
  const [openDeductFuelModal, setOpenDeductFuelModal] = useState(false);
  const [editingFuelClient, setEditingFuelClient] = useState(null);
  const { responseInfo, isInfoPending } = useOutletContext();

  const navigate = useNavigate();
  const location = useLocation();
  const isNewClient = location.state?.newClient;
  // const isNewClient = true;
  const { t, i18n } = useTranslation();
  const formatter = createCurrencyFormatter(i18n.language);

  const { guid } = useParams();
  const data = queryClient.getQueryData(["getInfo"]);
  const item = data?.data?.data?.clients?.find((i) => {
    if (i.guid === guid) return true;

    if (Array.isArray(i.farms)) {
      return i.farms.some((farm) => farm.guid === guid);
    }

    return false;
  });
  const { fuel_balance, cost, balance, client, autoapprove } = item || {};

  const mutationEnableChat = useEnableChat();
  const settingsMutation = useAddClientSettings();

  const isGuidInClients = isInfoPending
    ? true
    : responseInfo?.clients?.some((client) => {
        if (client.guid === guid) return true;
        if (client.kind === "Farmer" && Array.isArray(client.farms)) {
          return client.farms.some((farm) => farm.guid === guid);
        }
        return false;
      });
  // const isGuidInClients = false;

  const names = chatList?.map((c) => c.name) ?? [];

  const showSuccessToast = (title, subtitle) => {
    const toastId = toast(
      ({ closeToast }) => (
        <ToastNotification
          kind="success"
          title={title}
          subtitle={subtitle}
          hideCloseButton
          statusIconDescription=""
          className="toast_notification"
          aria-label={t("main.modalText.close")}
        />
      ),
      {
        containerId: "admin_notifications",
        autoClose: false,
        closeButton: false,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
      },
    );

    setTimeout(() => {
      toast.dismiss(toastId);
    }, 3000);
  };

  const handleFuelEdit = () => {
    setEditingFuelClient({
      guid: guid,
      client: client,
      fuel_balance: fuel_balance,
      balance: balance,
      cost: cost,
    });
    setOpenTopUpFuelModal(true);
  };

  const handleDeductFuelEdit = () => {
    setEditingFuelClient({
      guid: guid,
      client: client,
      fuel_balance: fuel_balance,
      balance: balance,
      cost: cost,
    });
    setOpenDeductFuelModal(true);
  };

  const handleBack = () => {
    clearRoute("admin");
    navigate("/");
    setClientInfo([]);
    setChatList([]);
  };

  useEffect(() => {
    ReactGA.event("screen_view", {
      firebase_screen: "ChatInfoSubpage",
      firebase_screen_class: "AdminPage",
    });
  }, []);

  const {
    isPending: isClientInfoPending,
    isSuccess: isClientInfoSuccess,
    error: clientInfoError,
    data: clientInfoData,
  } = useGetChatClientInfo({ guid });

  useEffect(() => {
    if (clientInfoError) {
      clearRoute("admin");
      navigate("/error", {
        state: {
          code: clientInfoError.code,
          message: clientInfoError.message,
        },
      });
    }
  }, [clientInfoError]);

  useEffect(() => {
    if (isClientInfoSuccess) {
      if (clientInfoData?.error) {
        clearRoute("admin");
        navigate("/error", {
          state: {
            code: "200",
            message: clientInfoData.error,
          },
        });
        return;
      }

      saveRoute("admin", {
        pathname: location.pathname,
        state: location.state,
      });

      setClientInfo(clientInfoData);
      setIsChatActivated(clientInfoData.canchat);
    }
  }, [isClientInfoSuccess, clientInfoData]);

  const {
    isPending: isChatListPending,
    isSuccess: isChatListSuccess,
    error: chatListError,
    data: chatListData,
  } = useGetChatList({ guid });

  useEffect(() => {
    if (chatListError) {
      clearRoute("admin");
      navigate("/error", {
        state: {
          code: chatListError.code,
          message: chatListError.message,
        },
      });
    }
  }, [chatListError]);

  useEffect(() => {
    if (isChatListSuccess) {
      if (chatListData?.error) {
        clearRoute("admin");
        navigate("/error", {
          state: {
            code: "200",
            message: chatListData.error,
          },
        });
        return;
      }
      setChatList(chatListData);
    }
  }, [isChatListSuccess, chatListData]);

  const handleEnableChat = () => {
    mutationEnableChat.mutate(
      { guid },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["chat_client_info"] });
        },
        onError: (error) => {
          clearRoute("admin");
          navigate("/error", {
            state: {
              code: error.code,
              message: error.message,
            },
          });
        },
      },
    );
  };

  const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(window.matchMedia(query).matches);

    useEffect(() => {
      const media = window.matchMedia(query);
      const listener = () => setMatches(media.matches);

      media.addEventListener("change", listener);
      return () => media.removeEventListener("change", listener);
    }, [query]);

    return matches;
  };

  const isMobile = useMediaQuery("(max-width: 750px)");

  const handleChangeAutoapproveSettings = (checked) => {
    settingsMutation.mutate(
      {
        guid,
        autoapprove: checked,
        cost: Number(cost ?? 0)
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["getInfo"],
          });
        },
        onError: (error) => {
          clearRoute("admin");
          navigate("/error", {
            state: {
              code: error.code,
              message: error.message,
            },
          });
        },
      },
    );
  };

  return (
    <div>
      <div className="main_chat_header">
        <div className="main_chat_header_back">
          <Button
            onClick={handleBack}
            renderIcon={ArrowLeft}
            hasIconOnly
            iconDescription={`${t("main.buttons.back")}`}
            kind="ghost"
            tooltipPosition="bottom"
            size="sm"
          />
          <p>{t("main.header.admin")}</p>
        </div>
      </div>
      <Tile id="tile-1" className="admin">
        <div className="admin_info">
          <div className="admin_info_balance">
            <span>
              {client} {t("main.chat.chatClientsTable.fuel_balance")}
            </span>
            <div className="admin_info_balance_fuel">
              <h1 className={fuel_balance < 0 ? "negative" : undefined}>
                <FuelIcon className="admin_info_balance_fuel_img" />
                {`${fuel_balance || 0}`}
              </h1>
              <div className="admin_info_balance_info">
                <span>
                  {t("main.admin.top_up_fuel.modal.costDesc", {
                    cost: Number(
                      (Number(cost) * 100).toPrecision(12),
                    ).toString(),
                  })}
                </span>
                <span>
                  {t("main.chat.balance", {
                    balance: formatter.format(normalizeNumber(balance) || 0),
                  })}
                </span>
              </div>
            </div>
            <div className="admin_info_balance_autoapprove">
              <p>{t("main.chat.chatClientsTable.autoapprove")}</p>
              <Toggle
                toggled={Boolean(autoapprove)}
                id="toggle-autoapprove"
                labelA={t("main.chat.chatClientsTable.autoapproveOff")}
                labelB={t("main.chat.chatClientsTable.autoapproveOn")}
                labelText=""
                onToggle={(checked) => handleChangeAutoapproveSettings(checked)}
                disabled={settingsMutation.isPending}
              />
            </div>
          </div>
        </div>
        <div className="admin_info_buttons">
          {isClientInfoPending || isChatActivated === undefined ? (
            <ButtonSkeleton></ButtonSkeleton>
          ) : (
            <Button
              onClick={handleEnableChat}
              disabled={isChatActivated}
              className="admin_info_buttons_activate"
            >
              {isChatActivated
                ? t("main.chat.chatActivatedButton")
                : t("main.chat.activateChatButton")}
            </Button>
          )}
          <Button
            onClick={handleFuelEdit}
            className="admin_info_buttons_top_up"
          >
            {t("main.admin.top_up_fuel.title")}
          </Button>
          <Button
            onClick={handleDeductFuelEdit}
            className="admin_info_buttons_deduct fuel"
          >
            {t("main.admin.deduct_fuel.title")}
          </Button>
        </div>
      </Tile>
      {isClientInfoPending || isChatListPending ? (
        <SkeletonPlaceholder style={{ width: "100%" }} />
      ) : (
        <>
          <div className="main_chat_tabs">
            <CoworkersDataTable
              data={clientInfo}
              pending={isClientInfoPending}
              guid={guid}
              isMobile={isMobile}
            />
            <ChatManagerDataTable
              data={chatList}
              pending={isChatListPending}
              guid={guid}
              client={client}
              isMobile={isMobile}
            />
          </div>
          <div className="main_chat_balance_history">
            <ChatBalanceHistoryDataTable guid={guid} />
          </div>
        </>
      )}

      {!isGuidInClients && isNewClient && openClientBindingModal && (
        <ClientBindingModal
          open={openClientBindingModal}
          setOpen={setOpenClientBindingModal}
          guid={guid}
          allChatNames={names}
          clientCoworkers={clientInfo?.coworkers}
          isClientInfoPending={isClientInfoPending}
          userPublicName={responseInfo?.publicName}
          setToastType={setToastType}
        />
      )}

      {toastType && isGuidInClients === false && (
        <ToastNotification
          kind={toastType}
          title={t(
            toastType === "success"
              ? "authentication.notification.addNewClientSuccess.title"
              : "authentication.notification.addNewClientError.title",
          )}
          subtitle={t(
            toastType === "success"
              ? "authentication.notification.addNewClientSuccess.descError"
              : "authentication.notification.addNewClientError.descError",
          )}
          onClose={() => {
            setToastType(null);
          }}
          timeout={6000}
          className="main_notification"
          statusIconDescription=""
          aria-label={t("main.modalText.close")}
        />
      )}
      {openTopUpFuelModal && (
        <TopUpFuelModal
          openModal={openTopUpFuelModal}
          setOpenModal={setOpenTopUpFuelModal}
          editingClient={editingFuelClient}
          onSuccess={() =>
            showSuccessToast(
              t("authentication.notification.success.title"),
              t("main.admin.top_up_fuel.topped_up"),
            )
          }
        />
      )}
      {openDeductFuelModal && (
        <DeductFuelModal
          openModal={openDeductFuelModal}
          setOpenModal={setOpenDeductFuelModal}
          editingClient={editingFuelClient}
          onSuccess={() =>
            showSuccessToast(
              t("authentication.notification.success.title"),
              t("main.admin.deduct_fuel.deducted"),
            )
          }
        />
      )}
      <ToastContainer
        containerId="admin_notifications"
        position="bottom-right"
        transition={Slide}
        icon={false}
        hideProgressBar
        pauseOnHover={false}
        pauseOnFocusLoss={false}
      />
    </div>
  );
}
